import axios from 'axios';
import { PEOPLE_API_PATH } from 'constants/api';
import { PeopleInterface } from 'type/peopleType';
import { CastData, CastApiRequest } from '../../types/i18n/i18nTypes';
import { FetchResult } from '../../type/apiType';
import { ApiService } from './apiService';

class PeopleApiService extends ApiService<PeopleInterface> {
  /**
   * i18n 필드를 포함한 Cast 목록 조회
   */
  async fetchCastWithI18n(params?: URLSearchParams): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);

    // i18n 필드를 포함한 필드 목록 설정
    const fields = [
      'id',
      'name',
      'name_en',
      'i18n_name',
      'birthdate',
      'nationality',
      'thumbnail',
      'type',
      'slug',
      'status',
      'created',
      'modified',
    ].join(',');

    searchParams.set('fields', fields);

    const response = await axios.get<FetchResult<CastData>>(this.constructUrl(), {
      params: searchParams,
    });

    return response.data;
  }

  /**
   * i18n 필드를 포함한 단일 Cast 조회
   */
  async fetchCastByIdWithI18n(id: number): Promise<CastData> {
    const fields = [
      'id',
      'name',
      'name_en',
      'i18n_name',
      'birthdate',
      'nationality',
      'thumbnail',
      'type',
      'slug',
      'status',
      'created',
      'modified',
    ].join(',');

    const response = await axios.get<CastData>(this.constructUrl(id), {
      params: { fields },
    });

    return response.data;
  }

  /**
   * i18n 필드를 포함한 Cast 생성
   */
  async createCastWithI18n(castData: CastApiRequest): Promise<CastData> {
    const response = await axios.post<CastData>(this.constructUrl(), castData);
    return response.data;
  }

  /**
   * i18n 필드를 포함한 Cast 업데이트
   */
  async updateCastWithI18n(id: number, castData: Partial<CastApiRequest>): Promise<CastData> {
    const response = await axios.patch<CastData>(this.constructUrl(id), castData);
    return response.data;
  }

  /**
   * 특정 언어의 Cast 목록 조회
   */
  async fetchCastByLanguage(
    language: string,
    params?: URLSearchParams
  ): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);
    searchParams.set('language', language);

    return this.fetchCastWithI18n(searchParams);
  }

  /**
   * 번역 완성도별 Cast 조회
   */
  async fetchCastByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    params?: URLSearchParams
  ): Promise<FetchResult<CastData>> {
    const searchParams = new URLSearchParams(params);
    searchParams.set('required_languages', requiredLanguages.join(','));

    return this.fetchCastWithI18n(searchParams);
  }
}

export const peopleApiService = new PeopleApiService(PEOPLE_API_PATH);
